import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  IndianRupee,
  Loader2,
  MapPin,
  PackageCheck,
  Plus,
  Save,
  Search,
  Truck,
  X,
} from "lucide-react";
import { restaurantAdminService, rowsOf } from "../../services/restaurantAdmin.service";
import { settingsService } from "../../services/settings.service";
import { Button, Card, ErrorBox, PageHeader, Switch } from "./components/FoodAdminUi";

const PINCODE_API_BASE = "https://pincode.indraq.info/api/v1";

const DEFAULT_FOOD_DELIVERY_FEE_RULE = Object.freeze({
  enabled: true,
  currency: "INR",
  baseKm: 3,
  baseFee: 40,
  perKmCharge: 8,
  minimumFee: 10,
  maxFee: 80,
  freeDeliveryAbove: 500,
  roundTo: 1,
});

const DEFAULT_FOOD_DRIVER_EARNING_RULE = Object.freeze({
  enabled: true,
  currency: "INR",
  mode: "DISTANCE_BASED",
  fixedAmount: 35,
  baseKm: 3,
  baseFee: 25,
  perKmCharge: 8,
  minimumFee: 25,
  maxFee: 100,
  roundTo: 1,
});

const DEFAULT_FOOD_RAIN_CHARGE_RULE = Object.freeze({
  enabled: false,
  currency: "INR",
  amount: 0,
  minOrderValue: 0,
  minProbabilityPercent: 50,
  cacheTtlSeconds: 300,
  requireRainType: true,
  applyWhenWeatherApiFails: false,
  restaurantOverrides: [],
});

function parseJsonMaybe(value) {
  if (typeof value !== "string") return value;
  const text = value.trim();
  if (!text) return undefined;
  if (!text.startsWith("{") && !text.startsWith("[")) return value;
  try {
    return JSON.parse(text);
  } catch {
    return value;
  }
}

function unwrapData(res) {
  let current = res?.data ?? res ?? {};

  for (let i = 0; i < 6; i += 1) {
    current = parseJsonMaybe(current);
    if (!current || typeof current !== "object" || Array.isArray(current)) break;

    if (current.item) {
      current = current.item;
      continue;
    }
    if (current.setting_value !== undefined) {
      current = current.setting_value;
      continue;
    }
    if (current.raw_setting_value !== undefined) {
      current = current.raw_setting_value;
      continue;
    }
    if (current.value !== undefined) {
      current = current.value;
      continue;
    }
    if (current.data && Object.keys(current).length <= 2) {
      current = current.data;
      continue;
    }
    break;
  }

  return current && typeof current === "object" ? current : {};
}

function numberOr(value, fallback) {
  const parsed = parseJsonMaybe(value);
  const n = Number(parsed);
  return Number.isFinite(n) ? n : fallback;
}

function boolOr(value, fallback) {
  const parsed = parseJsonMaybe(value);
  if (parsed === undefined || parsed === null || parsed === "") return fallback;
  if (typeof parsed === "boolean") return parsed;
  if (typeof parsed === "number") return parsed !== 0;
  const text = String(parsed).trim().toLowerCase();
  if (["true", "1", "yes", "y", "on", "enabled", "active"].includes(text)) return true;
  if (["false", "0", "no", "n", "off", "disabled", "inactive"].includes(text)) return false;
  return fallback;
}

function pickFirst(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== "");
}

function pickNested(obj, paths = []) {
  for (const path of paths) {
    const parts = Array.isArray(path) ? path : String(path).split(".");
    let current = obj;
    for (const part of parts) {
      if (!current || typeof current !== "object" || !(part in current)) {
        current = undefined;
        break;
      }
      current = parseJsonMaybe(current[part]);
    }
    if (current !== undefined && current !== null && current !== "") return current;
  }
  return undefined;
}

function isDeliveryFeeRuleLike(value) {
  const rule = parseJsonMaybe(value);
  if (!rule || typeof rule !== "object" || Array.isArray(rule)) return false;
  return [
    "enabled", "isEnabled", "is_enabled",
    "currency",
    "baseKm", "base_km",
    "baseFee", "base_fee",
    "perKmCharge", "per_km_charge",
    "minimumFee", "minimum_fee",
    "maxFee", "max_fee",
    "freeDeliveryAbove", "free_delivery_above",
    "roundTo", "round_to",
  ].some((key) => rule[key] !== undefined && rule[key] !== null && rule[key] !== "");
}

function isDriverEarningRuleLike(value) {
  const rule = parseJsonMaybe(value);
  if (!rule || typeof rule !== "object" || Array.isArray(rule)) return false;
  return [
    "enabled", "isEnabled", "is_enabled",
    "currency",
    "mode", "calculationMode", "calculation_mode",
    "fixedAmount", "fixed_amount", "amount",
    "baseKm", "base_km",
    "baseFee", "base_fee",
    "perKmCharge", "per_km_charge",
    "minimumFee", "minimum_fee",
    "maxFee", "max_fee",
    "roundTo", "round_to",
  ].some((key) => rule[key] !== undefined && rule[key] !== null && rule[key] !== "");
}

function normalizeDriverEarningMode(value, fallback = "DISTANCE_BASED") {
  const text = String(value || fallback || "DISTANCE_BASED").trim().toUpperCase();
  if (["FIXED", "FIX", "FLAT", "FLAT_RATE"].includes(text)) return "FIXED";
  return "DISTANCE_BASED";
}

function readFoodDeliveryFeeRule(data = {}) {
  const mode = data.businessMode || data.business_mode || {};
  const src = data.foodDeliverySettings || data.food_delivery_settings || mode.foodDeliverySettings || mode.food_delivery_settings || {};

  const foodRule = pickFirst(
    data.foodDeliveryFeeRule,
    data.food_delivery_fee_rule,
    data.foodDeliveryFee,
    data.food_delivery_fee,
    src.deliveryFeeRule,
    src.delivery_fee_rule,
    src.deliveryFee,
    src.delivery_fee,
    mode.foodDeliveryFeeRule,
    mode.foodDeliveryFee,
    mode.foodDeliverySettings?.deliveryFeeRule,
    mode.foodDeliverySettings?.deliveryFee,
    mode.food_delivery_settings?.delivery_fee_rule,
    mode.food_delivery_settings?.delivery_fee,
    data.deliveryFeeRules?.food,
    data.delivery_fee_rules?.food,
    pickNested(data, [
      "businessMode.foodDeliveryFeeRule",
      "businessMode.foodDeliveryFee",
      "businessMode.foodDeliverySettings.deliveryFeeRule",
      "businessMode.foodDeliverySettings.deliveryFee",
      "business_mode.foodDeliveryFeeRule",
      "business_mode.foodDeliveryFee",
      "business_mode.foodDeliverySettings.deliveryFeeRule",
      "business_mode.foodDeliverySettings.deliveryFee",
      "foodDeliverySettings.deliveryFeeRule",
      "foodDeliverySettings.deliveryFee",
      "food_delivery_settings.delivery_fee_rule",
      "food_delivery_settings.delivery_fee",
      "deliveryFeeRules.food",
      "delivery_fee_rules.food",
    ])
  );

  const groceryFallback = pickFirst(
    data.deliveryFee,
    data.delivery_fee,
    data.delivery?.deliveryFee,
    data.delivery?.delivery_fee,
    data.deliveryFeeRules?.grocery,
    data.delivery_fee_rules?.grocery
  );

  const directRule = isDeliveryFeeRuleLike(data) ? data : null;
  const rule = parseJsonMaybe(directRule) || parseJsonMaybe(foodRule) || parseJsonMaybe(groceryFallback) || DEFAULT_FOOD_DELIVERY_FEE_RULE;

  return {
    enabled: boolOr(rule.enabled ?? rule.isEnabled ?? rule.is_enabled, DEFAULT_FOOD_DELIVERY_FEE_RULE.enabled),
    currency: String(rule.currency || DEFAULT_FOOD_DELIVERY_FEE_RULE.currency),
    baseKm: numberOr(rule.baseKm ?? rule.base_km, DEFAULT_FOOD_DELIVERY_FEE_RULE.baseKm),
    baseFee: numberOr(rule.baseFee ?? rule.base_fee, DEFAULT_FOOD_DELIVERY_FEE_RULE.baseFee),
    perKmCharge: numberOr(rule.perKmCharge ?? rule.per_km_charge, DEFAULT_FOOD_DELIVERY_FEE_RULE.perKmCharge),
    minimumFee: numberOr(rule.minimumFee ?? rule.minimum_fee, DEFAULT_FOOD_DELIVERY_FEE_RULE.minimumFee),
    maxFee: numberOr(rule.maxFee ?? rule.max_fee, DEFAULT_FOOD_DELIVERY_FEE_RULE.maxFee),
    freeDeliveryAbove: numberOr(rule.freeDeliveryAbove ?? rule.free_delivery_above, DEFAULT_FOOD_DELIVERY_FEE_RULE.freeDeliveryAbove),
    roundTo: numberOr(rule.roundTo ?? rule.round_to, DEFAULT_FOOD_DELIVERY_FEE_RULE.roundTo),
  };
}


function readFoodDriverEarningRule(data = {}) {
  const mode = data.businessMode || data.business_mode || {};
  const src = data.foodDeliverySettings || data.food_delivery_settings || mode.foodDeliverySettings || mode.food_delivery_settings || {};

  const foodRule = pickFirst(
    data.foodDriverEarningRule,
    data.food_driver_earning_rule,
    data.foodDriverEarning,
    data.food_driver_earning,
    data.driverEarningRules?.food,
    data.driver_earning_rules?.food,
    src.driverEarningRule,
    src.driver_earning_rule,
    src.driverEarning,
    src.driver_earning,
    mode.foodDriverEarningRule,
    mode.food_driver_earning_rule,
    mode.foodDriverEarning,
    mode.food_driver_earning,
    mode.driverEarningRules?.food,
    mode.driver_earning_rules?.food,
    mode.foodDeliverySettings?.driverEarningRule,
    mode.foodDeliverySettings?.driver_earning_rule,
    mode.foodDeliverySettings?.driverEarning,
    mode.foodDeliverySettings?.driver_earning,
    mode.food_delivery_settings?.driverEarningRule,
    mode.food_delivery_settings?.driver_earning_rule,
    mode.food_delivery_settings?.driverEarning,
    mode.food_delivery_settings?.driver_earning,
    mode.food?.delivery?.driverEarningRule,
    mode.food?.delivery?.driver_earning_rule,
    mode.food?.delivery?.driverEarning,
    mode.food?.delivery?.driver_earning,
    pickNested(data, [
      "businessMode.food.delivery.driverEarningRule",
      "businessMode.food.delivery.driver_earning_rule",
      "businessMode.foodDeliverySettings.driverEarningRule",
      "businessMode.foodDeliverySettings.driver_earning_rule",
      "business_mode.food.delivery.driverEarningRule",
      "business_mode.food.delivery.driver_earning_rule",
      "business_mode.foodDeliverySettings.driverEarningRule",
      "business_mode.foodDeliverySettings.driver_earning_rule",
      "foodDeliverySettings.driverEarningRule",
      "foodDeliverySettings.driver_earning_rule",
      "food_delivery_settings.driverEarningRule",
      "food_delivery_settings.driver_earning_rule",
      "driverEarningRules.food",
      "driver_earning_rules.food",
    ])
  );

  const globalFallback = pickFirst(
    data.driverEarningRule,
    data.driver_earning_rule,
    mode.driverEarningRule,
    mode.driver_earning_rule
  );

  const directRule = isDriverEarningRuleLike(data) ? data : null;
  const rule = parseJsonMaybe(directRule) || parseJsonMaybe(foodRule) || parseJsonMaybe(globalFallback) || DEFAULT_FOOD_DRIVER_EARNING_RULE;

  return normalizeDriverEarningRule(rule);
}

function readFoodDeliverySettings(data = {}) {
  const mode = data.businessMode || data.business_mode || {};
  const src =
    data.foodDeliverySettings ||
    data.food_delivery_settings ||
    mode.foodDeliverySettings ||
    mode.food_delivery_settings ||
    data.settings?.foodDeliverySettings ||
    data.settings?.food_delivery_settings ||
    {};

  const platformFeeValue = pickFirst(
    data.platformFee,
    data.platform_fee,
    data.foodPlatformFee,
    data.food_platform_fee,
    src.platformFee,
    src.platform_fee,
    src.foodPlatformFee,
    src.food_platform_fee,
    mode.platformFee,
    mode.platform_fee,
    mode.foodPlatformFee,
    mode.food_platform_fee
  );

  const packagingChargeValue = pickFirst(
    data.packagingCharge,
    data.packaging_charge,
    data.foodPackagingCharge,
    data.food_packaging_charge,
    src.packagingCharge,
    src.packaging_charge,
    src.foodPackagingCharge,
    src.food_packaging_charge,
    mode.packagingCharge,
    mode.packaging_charge,
    mode.foodPackagingCharge,
    mode.food_packaging_charge
  );

  const smallCartSource = parseJsonMaybe(pickFirst(
    data.smallCartCharge,
    data.foodSmallCartCharge,
    src.smallCartCharge,
    mode.food?.fees?.smallCartCharge
  )) || {};

  const packagingThreshold = pickFirst(
    data.packagingChargeApplyAbove,
    data.packaging_charge_apply_above,
    data.packagingChargeMinOrderValue,
    data.packaging_charge_min_order_value,
    data.packagingApplyAbove,
    data.packaging_apply_above,
    src.packagingChargeApplyAbove,
    src.packaging_charge_apply_above,
    src.packagingChargeMinOrderValue,
    src.packaging_charge_min_order_value,
    src.packagingApplyAbove,
    src.packaging_apply_above,
    mode.packagingChargeApplyAbove,
    mode.packaging_charge_apply_above,
    mode.packagingChargeMinOrderValue,
    mode.packaging_charge_min_order_value
  );

  return {
    deliveryRadiusKm: numberOr(
      pickFirst(data.deliveryRadiusKm, data.delivery_radius_km, src.deliveryRadiusKm, src.delivery_radius_km),
      5
    ),
    minimumOrderPrice: numberOr(
      pickFirst(data.minimumOrderPrice, data.minimum_order_price, src.minimumOrderPrice, src.minimum_order_price),
      0
    ),
    smallCartCharge: {
      enabled: boolOr(smallCartSource.enabled, false),
      amount: numberOr(pickFirst(smallCartSource.amount, smallCartSource.charge, smallCartSource.fee), 0),
    },
    platformFee: numberOr(platformFeeValue, 0),
    packagingCharge: numberOr(packagingChargeValue, 0),
    packagingChargeApplyAbove: numberOr(packagingThreshold, 0),
    deliveryFeeRule: readFoodDeliveryFeeRule(data),
    driverEarningRule: readFoodDriverEarningRule(data),
    rainCharge: readFoodRainChargeRule(data),
  };
}

function normalizeDeliveryFeeRule(rule = {}) {
  return {
    enabled: Boolean(rule.enabled),
    currency: String(rule.currency || "INR").trim().toUpperCase() || "INR",
    baseKm: Math.max(0, Number(rule.baseKm || 0)),
    baseFee: Math.max(0, Number(rule.baseFee || 0)),
    perKmCharge: Math.max(0, Number(rule.perKmCharge || 0)),
    minimumFee: Math.max(0, Number(rule.minimumFee || 0)),
    maxFee: Math.max(0, Number(rule.maxFee || 0)),
    freeDeliveryAbove: Math.max(0, Number(rule.freeDeliveryAbove || 0)),
    roundTo: Math.max(0.01, Number(rule.roundTo || 1)),
  };
}

function normalizeDriverEarningRule(rule = {}) {
  const src = parseJsonMaybe(rule) || {};
  return {
    enabled: boolOr(src.enabled ?? src.isEnabled ?? src.is_enabled, DEFAULT_FOOD_DRIVER_EARNING_RULE.enabled),
    currency: String(src.currency || DEFAULT_FOOD_DRIVER_EARNING_RULE.currency).trim().toUpperCase() || "INR",
    mode: normalizeDriverEarningMode(src.mode ?? src.calculationMode ?? src.calculation_mode, DEFAULT_FOOD_DRIVER_EARNING_RULE.mode),
    fixedAmount: Math.max(0, Number(src.fixedAmount ?? src.fixed_amount ?? src.amount ?? DEFAULT_FOOD_DRIVER_EARNING_RULE.fixedAmount)),
    baseKm: Math.max(0, Number(src.baseKm ?? src.base_km ?? DEFAULT_FOOD_DRIVER_EARNING_RULE.baseKm)),
    baseFee: Math.max(0, Number(src.baseFee ?? src.base_fee ?? DEFAULT_FOOD_DRIVER_EARNING_RULE.baseFee)),
    perKmCharge: Math.max(0, Number(src.perKmCharge ?? src.per_km_charge ?? DEFAULT_FOOD_DRIVER_EARNING_RULE.perKmCharge)),
    minimumFee: Math.max(0, Number(src.minimumFee ?? src.minimum_fee ?? DEFAULT_FOOD_DRIVER_EARNING_RULE.minimumFee)),
    maxFee: Math.max(0, Number(src.maxFee ?? src.max_fee ?? DEFAULT_FOOD_DRIVER_EARNING_RULE.maxFee)),
    roundTo: Math.max(0.01, Number(src.roundTo ?? src.round_to ?? DEFAULT_FOOD_DRIVER_EARNING_RULE.roundTo)),
  };
}


function normalizeRainChargeRestaurantOverrides(overrides = []) {
  if (!Array.isArray(overrides)) return [];
  return overrides
    .map((item) => ({
      restaurantId: String(item?.restaurantId || item?.restaurant_id || item?.id || "").trim(),
      restaurantName: item?.restaurantName || item?.restaurant_name || item?.name || "",
      enabled: boolOr(item?.enabled, false),
      currency: String(item?.currency || "INR").trim().toUpperCase() || "INR",
      amount: Math.max(0, Number(item?.amount || item?.charge || 0)),
      minOrderValue: Math.max(0, Number(item?.minOrderValue ?? item?.min_order_value ?? 0)),
      minProbabilityPercent: Math.min(100, Math.max(0, Number(item?.minProbabilityPercent ?? item?.min_probability_percent ?? 50))),
      cacheTtlSeconds: Math.max(30, Number(item?.cacheTtlSeconds ?? item?.cache_ttl_seconds ?? 300)),
    }))
    .filter((item) => item.restaurantId);
}

function normalizeRainChargeRule(rule = {}) {
  const src = parseJsonMaybe(rule) || {};
  return {
    enabled: boolOr(src.enabled ?? src.isEnabled ?? src.is_enabled, DEFAULT_FOOD_RAIN_CHARGE_RULE.enabled),
    currency: String(src.currency || DEFAULT_FOOD_RAIN_CHARGE_RULE.currency).trim().toUpperCase() || "INR",
    amount: Math.max(0, Number(src.amount ?? src.charge ?? DEFAULT_FOOD_RAIN_CHARGE_RULE.amount)),
    minOrderValue: Math.max(0, Number(src.minOrderValue ?? src.min_order_value ?? DEFAULT_FOOD_RAIN_CHARGE_RULE.minOrderValue)),
    minProbabilityPercent: Math.min(100, Math.max(0, Number(src.minProbabilityPercent ?? src.min_probability_percent ?? DEFAULT_FOOD_RAIN_CHARGE_RULE.minProbabilityPercent))),
    cacheTtlSeconds: Math.max(30, Number(src.cacheTtlSeconds ?? src.cache_ttl_seconds ?? DEFAULT_FOOD_RAIN_CHARGE_RULE.cacheTtlSeconds)),
    requireRainType: true,
    applyWhenWeatherApiFails: false,
    restaurantOverrides: normalizeRainChargeRestaurantOverrides(src.restaurantOverrides || src.restaurant_overrides || []),
  };
}

function readFoodRainChargeRule(data = {}) {
  const mode = data.businessMode || data.business_mode || {};
  const settings = data.foodDeliverySettings || data.food_delivery_settings || mode.foodDeliverySettings || mode.food_delivery_settings || {};
  const raw = pickFirst(
    data.foodRainCharge,
    data.food_rain_charge,
    data.rainCharge?.food,
    data.rain_charge?.food,
    settings.rainCharge,
    settings.rain_charge,
    mode.foodRainCharge,
    mode.food_rain_charge,
    mode.foodDeliverySettings?.rainCharge,
    mode.foodDeliverySettings?.rain_charge,
    mode.food_delivery_settings?.rainCharge,
    mode.food_delivery_settings?.rain_charge,
    pickNested(data, [
      "businessMode.foodDeliverySettings.rainCharge",
      "businessMode.foodDeliverySettings.rain_charge",
      "business_mode.foodDeliverySettings.rainCharge",
      "business_mode.food_delivery_settings.rain_charge",
      "foodDeliverySettings.rainCharge",
      "foodDeliverySettings.rain_charge",
      "food_delivery_settings.rainCharge",
      "food_delivery_settings.rain_charge",
    ])
  );
  return normalizeRainChargeRule(raw || DEFAULT_FOOD_RAIN_CHARGE_RULE);
}

function addEmptyRestaurantRainOverride(list = []) {
  return [
    ...list,
    {
      restaurantId: "",
      restaurantName: "",
      enabled: false,
      currency: "INR",
      amount: 0,
      minOrderValue: 0,
      minProbabilityPercent: 50,
      cacheTtlSeconds: 300,
    },
  ];
}

function cleanName(value) {
  return String(value || "").trim().replace(/\s+/g, " ");
}

function token(value) {
  return cleanName(value).toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function uniqueList(value) {
  const parsed = parseJsonMaybe(value);
  const raw = Array.isArray(parsed) ? parsed : String(parsed || "").split(/[\n,|]+/g);
  const seen = new Set();
  const out = [];

  raw.forEach((item) => {
    const label = cleanName(typeof item === "string" ? item : item?.name || item?.label || item?.value || item?.district || item?.city || item?.pincode);
    const key = token(label);
    if (!label || !key || seen.has(key)) return;
    seen.add(key);
    out.push(label);
  });

  return out;
}

function normalizeStateList(value) {
  const parsed = parseJsonMaybe(value);
  let raw = parsed;

  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    raw = raw.enabledStates || raw.enabled_states || raw.states || raw.foodEnabledStates || raw.food_enabled_states || [];
  }

  return uniqueList(raw);
}

function normalizeLocationRules(value) {
  const parsed = parseJsonMaybe(value);
  const items = Array.isArray(parsed) ? parsed : [];
  const byState = new Map();

  items.forEach((item) => {
    if (!item) return;
    const state = cleanName(typeof item === "string" ? item : item.state || item.stateName || item.state_name);
    if (!state) return;
    const key = token(state);
    const current = byState.get(key) || {
      state,
      districts: [],
      cities: [],
      pincodes: [],
    };

    const districts = uniqueList(item.districts || item.district_names || item.district || item.districtName || item.district_name);
    const cities = uniqueList(item.cities || item.city_names || item.city || item.cityName || item.city_name);
    const pincodes = uniqueList(item.pincodes || item.pinCodes || item.pin_codes || item.pincode || item.postalCode || item.postal_code).map((pin) => pin.replace(/\D+/g, "")).filter(Boolean);

    current.districts = uniqueList([...current.districts, ...districts]);
    current.cities = uniqueList([...current.cities, ...cities]);
    current.pincodes = uniqueList([...current.pincodes, ...pincodes]).map((pin) => pin.replace(/\D+/g, "")).filter(Boolean);
    byState.set(key, current);
  });

  return Array.from(byState.values()).sort((a, b) => a.state.localeCompare(b.state));
}

function readFoodEnabledStates(data = {}) {
  const mode = data.businessMode || data.business_mode || {};
  const access = data.foodStateAccess || data.food_state_access || mode.foodStateAccess || mode.food_state_access || {};
  return normalizeStateList(
    pickFirst(
      data.foodEnabledStates,
      data.food_enabled_states,
      data.enabledFoodStates,
      data.enabled_food_states,
      access.enabledStates,
      access.enabled_states,
      access.states,
      mode.foodEnabledStates,
      mode.food_enabled_states,
      mode.enabledFoodStates,
      mode.enabled_food_states
    )
  );
}

function readFoodLocationRules(data = {}) {
  const mode = data.businessMode || data.business_mode || {};
  const access = data.foodLocationAccess || data.food_location_access || mode.foodLocationAccess || mode.food_location_access || {};
  const fromLocationRules = normalizeLocationRules(
    pickFirst(
      data.foodAllowedLocations,
      data.food_allowed_locations,
      data.allowedFoodLocations,
      data.allowed_food_locations,
      access.locations,
      access.rules,
      access.allowedLocations,
      access.allowed_locations,
      mode.foodAllowedLocations,
      mode.food_allowed_locations,
      mode.allowedFoodLocations,
      mode.allowed_food_locations,
      mode.foodLocationAccess?.locations,
      mode.foodLocationAccess?.rules,
      mode.food_location_access?.locations,
      mode.food_location_access?.rules
    )
  );

  if (fromLocationRules.length) return fromLocationRules;

  return readFoodEnabledStates(data).map((state) => ({
    state,
    districts: [],
    cities: [],
    pincodes: [],
  }));
}

function makeFoodFeePresetPayload(rule) {
  return {
    settingKey: "foodDeliveryFeeRule",
    setting_key: "foodDeliveryFeeRule",
    key: "foodDeliveryFeeRule",
    settingGroup: "delivery",
    setting_group: "delivery",
    group: "delivery",
    title: "Delivery Fee Rule",
    description: "Customer-facing delivery fee rule for food orders.",
    settingValue: rule,
    setting_value: rule,
    rawSettingValue: rule,
    raw_setting_value: rule,
    value: rule,
    data: rule,
    isPublic: false,
    is_public: false,
    isActive: true,
    is_active: true,
  };
}

async function readSavedFoodDeliveryFeeRule() {
  try {
    const res = await settingsService.getSetting("foodDeliveryFeeRule");
    const data = unwrapData(res);
    return readFoodDeliveryFeeRule(data);
  } catch {
    return null;
  }
}

async function saveSavedFoodDeliveryFeeRule(rule) {
  const payload = makeFoodFeePresetPayload(rule);
  try {
    await settingsService.upsertPreset("foodDeliveryFeeRule", payload);
    return true;
  } catch (firstError) {
    try {
      await settingsService.updateSetting("foodDeliveryFeeRule", payload);
      return true;
    } catch (secondError) {
      try {
        await settingsService.createSetting(payload);
        return true;
      } catch {
        throw firstError || secondError;
      }
    }
  }
}

function pickApiArray(payload) {
  const value = payload?.data ?? payload?.items ?? payload?.results ?? payload?.states ?? payload?.cities ?? payload;
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    return value.data || value.items || value.results || value.states || value.cities || value.postal || [];
  }
  return [];
}

function apiItemLabel(item) {
  if (typeof item === "string") return cleanName(item);
  return cleanName(item?.name || item?.state || item?.stateName || item?.state_name || item?.city || item?.cityName || item?.city_name || item?.district || item?.districtName || item?.district_name || item?.pincode || item?.postalCode || item?.postal_code);
}

function districtLabel(item) {
  if (typeof item === "string") return cleanName(item);
  return cleanName(
    item?.district ||
    item?.districtName ||
    item?.district_name ||
    item?.name ||
    item?.label ||
    item?.value
  );
}

function cityLabel(item) {
  if (typeof item === "string") return cleanName(item);
  return cleanName(
    item?.city ||
    item?.cityName ||
    item?.city_name ||
    item?.place ||
    item?.placeName ||
    item?.place_name ||
    item?.officeName ||
    item?.office_name ||
    item?.name ||
    item?.label ||
    item?.value
  );
}

function stateLabel(item) {
  if (typeof item === "string") return cleanName(item);
  return cleanName(item?.state || item?.stateName || item?.state_name || item?.name || item?.label || item?.value);
}

function readApiLocationRecord(item = {}) {
  if (!item || typeof item !== "object" || Array.isArray(item)) return null;
  const state = stateLabel(item);
  const district = districtLabel(item);
  const city = cityLabel(item);
  const pincode = cleanName(item.pincode || item.pinCode || item.pin_code || item.postalCode || item.postal_code || item.code).replace(/\D+/g, "");
  if (!state && !district && !city && !pincode) return null;
  return { state, district, city, pincode };
}

function extractLocationRecords(payload) {
  const out = [];
  const seen = new Set();

  function add(record) {
    if (!record) return;
    const key = `${token(record.state)}:${token(record.district)}:${token(record.city)}:${record.pincode || ""}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(record);
  }

  function visit(value) {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (typeof value !== "object") return;
    add(readApiLocationRecord(value));
    Object.values(value).forEach((child) => {
      if (child && typeof child === "object") visit(child);
    });
  }

  pickApiArray(payload).forEach(visit);
  if (!out.length) visit(payload);
  return out;
}

function mergeLocationRecords(...groups) {
  const seen = new Set();
  const out = [];
  groups.flat().forEach((record) => {
    if (!record) return;
    const normalized = {
      state: cleanName(record.state),
      district: cleanName(record.district),
      city: cleanName(record.city),
      pincode: cleanName(record.pincode).replace(/\D+/g, ""),
    };
    const key = `${token(normalized.state)}:${token(normalized.district)}:${token(normalized.city)}:${normalized.pincode}`;
    if ((!normalized.state && !normalized.district && !normalized.city && !normalized.pincode) || seen.has(key)) return;
    seen.add(key);
    out.push(normalized);
  });
  return out;
}

function filterRecordsByState(records = [], state = "") {
  const stateKey = token(state);
  if (!stateKey) return [];
  return records.filter((record) => !record.state || token(record.state) === stateKey);
}

function filterRecordsByDistrict(records = [], district = "") {
  const districtKey = token(district);
  if (!districtKey) return [];
  return records.filter((record) => record.district && token(record.district) === districtKey);
}

async function fetchJson(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json();
}

function findPostalRecord(value) {
  if (!value) return null;
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findPostalRecord(item);
      if (found) return found;
    }
    return null;
  }
  if (typeof value === "object") {
    const hasUsefulLocation =
      value.state || value.stateName || value.state_name ||
      value.district || value.districtName || value.district_name ||
      value.city || value.cityName || value.city_name ||
      value.pincode || value.postalCode || value.postal_code;
    if (hasUsefulLocation) return value;
    for (const item of Object.values(value)) {
      const found = findPostalRecord(item);
      if (found) return found;
    }
  }
  return null;
}

function getPostalLocation(record = {}) {
  return {
    state: cleanName(record.state || record.stateName || record.state_name || record.region || record.circle),
    district: cleanName(record.district || record.districtName || record.district_name),
    city: cleanName(record.city || record.cityName || record.city_name || record.name || record.officeName || record.office_name || record.postOffice || record.post_office),
    pincode: cleanName(record.pincode || record.pinCode || record.pin_code || record.postalCode || record.postal_code).replace(/\D+/g, ""),
  };
}

function normalizeFinalLocationRules(rules = []) {
  return normalizeLocationRules(rules).map((rule) => ({
    state: rule.state,
    districts: uniqueList(rule.districts),
    cities: uniqueList(rule.cities),
    pincodes: uniqueList(rule.pincodes).map((pin) => pin.replace(/\D+/g, "")).filter(Boolean),
  }));
}

function FeeInput({ label, value, onChange, min = 0, step = 1, prefix, suffix, placeholder }) {
  return (
    <label className="block">
      <span className="text-[13px] font-bold text-slate-700">{label}</span>
      <div className="mt-2 flex items-center gap-2 rounded-[18px] border border-rose-100 bg-white px-4 py-3">
        {prefix ? <span className="shrink-0 text-[16px] font-bold text-slate-400">{prefix}</span> : null}
        <input
          type="number"
          min={min}
          step={step}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent text-[15px] font-semibold text-[#d86a78] outline-none"
          placeholder={placeholder}
        />
        {suffix ? <span className="shrink-0 text-xs font-black uppercase tracking-[0.18em] text-slate-400">{suffix}</span> : null}
      </div>
    </label>
  );
}

function TextInput({ label, value, onChange, placeholder, disabled }) {
  return (
    <label className="block">
      <span className="text-[12px] font-black uppercase tracking-[0.18em] text-slate-500">{label}</span>
      <input
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 h-[48px] w-full rounded-[16px] border border-rose-100 bg-white px-4 text-[14px] font-semibold text-slate-700 outline-none transition focus:border-[#d86a78] disabled:bg-slate-50 disabled:text-slate-400"
        placeholder={placeholder}
      />
    </label>
  );
}

function SearchableDropdown({ label, value, onChange, options, placeholder, disabled, loading, allowCustom = false, emptyText = "No result found" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filteredOptions = useMemo(() => {
    const q = token(query);
    if (!q) return options.slice(0, 80);
    return options.filter((option) => token(option).includes(q)).slice(0, 80);
  }, [options, query]);
  const customValue = cleanName(query);
  const canUseCustom = allowCustom && customValue && !options.some((option) => token(option) === token(customValue));

  return (
    <div
      className="relative"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setOpen(false);
          setQuery("");
        }
      }}
    >
      <span className="text-[12px] font-black uppercase tracking-[0.18em] text-slate-500">{label}</span>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className="mt-2 flex h-[48px] w-full items-center justify-between gap-3 rounded-[16px] border border-rose-100 bg-white px-4 text-left text-[14px] font-semibold text-slate-700 outline-none transition hover:border-[#d86a78] disabled:bg-slate-50 disabled:text-slate-400"
      >
        <span className={value ? "truncate" : "truncate text-slate-400"}>{value || placeholder}</span>
        {loading ? <Loader2 size={16} className="shrink-0 animate-spin text-[#d86a78]" /> : <ChevronDown size={16} className="shrink-0 text-[#d86a78]" />}
      </button>

      {open && !disabled ? (
        <div className="absolute left-0 top-full z-40 mt-2 w-full overflow-hidden rounded-[18px] border border-rose-100 bg-white shadow-2xl shadow-rose-100/80">
          <div className="flex items-center gap-2 border-b border-rose-50 px-3 py-2">
            <Search size={15} className="text-slate-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-9 w-full bg-transparent text-[13px] font-semibold text-slate-700 outline-none placeholder:text-slate-400"
              placeholder="Search..."
            />
          </div>
          <div className="max-h-[240px] overflow-y-auto overscroll-contain p-1.5">
            {value ? (
              <button
                type="button"
                className="mb-1 flex w-full items-center rounded-[12px] px-3 py-2 text-left text-[13px] font-bold text-rose-600 hover:bg-rose-50"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange("");
                  setOpen(false);
                  setQuery("");
                }}
              >
                Clear selection
              </button>
            ) : null}
            {canUseCustom ? (
              <button
                type="button"
                className="mb-1 flex w-full items-center rounded-[12px] px-3 py-2 text-left text-[13px] font-black text-[#d86a78] hover:bg-rose-50"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(customValue);
                  setOpen(false);
                  setQuery("");
                }}
              >
                Use “{customValue}”
              </button>
            ) : null}
            {filteredOptions.length ? filteredOptions.map((option) => (
              <button
                key={option}
                type="button"
                className={`flex w-full items-center rounded-[12px] px-3 py-2 text-left text-[13px] font-bold transition ${token(option) === token(value) ? "bg-rose-50 text-[#d86a78]" : "text-slate-700 hover:bg-slate-50"}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(option);
                  setOpen(false);
                  setQuery("");
                }}
              >
                <span className="truncate">{option}</span>
              </button>
            )) : !canUseCustom ? (
              <div className="px-3 py-5 text-center text-[13px] font-bold text-slate-400">{emptyText}</div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Chip({ children, onRemove }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-rose-50 px-3 py-1.5 text-[12px] font-black text-[#d86a78]">
      <span className="truncate">{children}</span>
      {onRemove ? (
        <button type="button" onClick={onRemove} className="rounded-full p-0.5 hover:bg-rose-100">
          <X size={12} />
        </button>
      ) : null}
    </span>
  );
}

export default function FoodSettingsPage() {
  const [groceryEnabled, setGroceryEnabled] = useState(true);
  const [foodEnabled, setFoodEnabled] = useState(true);
  const [foodLocationRules, setFoodLocationRules] = useState([]);
  const [deliveryRadiusKm, setDeliveryRadiusKm] = useState(5);
  const [minimumOrderPrice, setMinimumOrderPrice] = useState(0);
  const [smallCartChargeEnabled, setSmallCartChargeEnabled] = useState(false);
  const [smallCartChargeAmount, setSmallCartChargeAmount] = useState(0);
  const [platformFee, setPlatformFee] = useState(0);
  const [packagingCharge, setPackagingCharge] = useState(0);
  const [packagingChargeApplyAbove, setPackagingChargeApplyAbove] = useState(0);
  const [foodDeliveryFeeRule, setFoodDeliveryFeeRule] = useState(DEFAULT_FOOD_DELIVERY_FEE_RULE);
  const [foodDriverEarningRule, setFoodDriverEarningRule] = useState(DEFAULT_FOOD_DRIVER_EARNING_RULE);
  const [foodRainChargeRule, setFoodRainChargeRule] = useState(DEFAULT_FOOD_RAIN_CHARGE_RULE);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [districtCities, setDistrictCities] = useState([]);
  const [statePostalRecords, setStatePostalRecords] = useState([]);
  const [pincodeOptions, setPincodeOptions] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [districtLoading, setDistrictLoading] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [draftLocation, setDraftLocation] = useState({ state: "", district: "", city: "", pincode: "" });

  useEffect(() => {
    let alive = true;
    setLoading(true);

    Promise.allSettled([
      restaurantAdminService.getBusinessMode(),
      readSavedFoodDeliveryFeeRule(),
      restaurantAdminService.listRestaurants({ page: 1, limit: 500 }),
    ])
      .then(([businessResult, foodFeeRuleResult, restaurantsResult]) => {
        if (!alive) return;
        const data = businessResult.status === "fulfilled" ? unwrapData(businessResult.value) : {};
        const mode = data.businessMode || data.business_mode || data;
        const foodSettings = readFoodDeliverySettings(data);
        const savedFoodFeeRule = foodFeeRuleResult.status === "fulfilled" ? foodFeeRuleResult.value : null;
        setGroceryEnabled(Boolean(mode.groceryEnabled ?? mode.grocery_enabled ?? true));
        setFoodEnabled(Boolean(mode.foodEnabled ?? mode.food_enabled ?? true));
        setFoodLocationRules(readFoodLocationRules(data));
        setDeliveryRadiusKm(foodSettings.deliveryRadiusKm);
        setMinimumOrderPrice(foodSettings.minimumOrderPrice);
        setSmallCartChargeEnabled(Boolean(foodSettings.smallCartCharge?.enabled));
        setSmallCartChargeAmount(Number(foodSettings.smallCartCharge?.amount || 0));
        setPlatformFee(foodSettings.platformFee);
        setPackagingCharge(foodSettings.packagingCharge);
        setPackagingChargeApplyAbove(foodSettings.packagingChargeApplyAbove);
        setFoodDeliveryFeeRule(savedFoodFeeRule || foodSettings.deliveryFeeRule || DEFAULT_FOOD_DELIVERY_FEE_RULE);
        setFoodDriverEarningRule(foodSettings.driverEarningRule || DEFAULT_FOOD_DRIVER_EARNING_RULE);
        setFoodRainChargeRule(foodSettings.rainCharge || DEFAULT_FOOD_RAIN_CHARGE_RULE);
        const restaurantRows = restaurantsResult.status === "fulfilled" ? rowsOf(restaurantsResult.value) : [];
        setRestaurants(restaurantRows.map((restaurant) => ({
          id: restaurant.id || restaurant.restaurantId || restaurant.restaurant_id,
          name: restaurant.name || restaurant.restaurantName || restaurant.restaurant_name || "Restaurant",
        })).filter((restaurant) => restaurant.id));
      })
      .catch(() => {
        // Settings endpoint failure must not break admin page. Defaults stay safe.
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    fetchJson(`${PINCODE_API_BASE}/states?country=IN`)
      .then((payload) => {
        if (!alive) return;
        const list = uniqueList(pickApiArray(payload).map(stateLabel)).sort((a, b) => a.localeCompare(b));
        setStates(list);
      })
      .catch(() => {
        if (alive) setStates([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    const state = cleanName(draftLocation.state);
    setDistricts([]);
    setCities([]);
    setDistrictCities([]);
    setStatePostalRecords([]);
    setPincodeOptions([]);
    if (!state) return undefined;

    setDistrictLoading(true);
    setCityLoading(true);

    const stateParam = encodeURIComponent(state);
    const firstWordParam = encodeURIComponent(state.split(/\s+/)[0] || state);

    Promise.all([
      fetchJson(`${PINCODE_API_BASE}/districts?country=IN&state=${stateParam}`).catch(() => null),
      fetchJson(`${PINCODE_API_BASE}/postal-search?q=${stateParam}&country=IN`).catch(() => null),
      fetchJson(`${PINCODE_API_BASE}/postal-search?q=${firstWordParam}&country=IN`).catch(() => null),
      fetchJson(`${PINCODE_API_BASE}/cities?country=IN&state=${stateParam}`).catch(() => null),
    ])
      .then(async ([districtPayload, stateSearchPayload, statePrefixPayload, cityPayload]) => {
        if (!alive) return;

        const cityItems = pickApiArray(cityPayload);
        const cityRecords = filterRecordsByState(extractLocationRecords(cityPayload), state);
        const stateSearchRecords = filterRecordsByState(extractLocationRecords(stateSearchPayload), state);
        const statePrefixRecords = filterRecordsByState(extractLocationRecords(statePrefixPayload), state);
        const districtItems = pickApiArray(districtPayload);
        const districtRecords = filterRecordsByState(extractLocationRecords(districtPayload), state);
        let records = mergeLocationRecords(districtRecords, stateSearchRecords, statePrefixRecords, cityRecords);

        let districtList = uniqueList([
          ...districtItems.map(districtLabel),
          ...records.map((record) => record.district),
        ]).sort((a, b) => a.localeCompare(b));

        const cityList = uniqueList([
          ...cityItems.map(cityLabel),
          ...cityRecords.map((record) => record.city),
        ]).sort((a, b) => a.localeCompare(b));

        if (!districtList.length && cityList.length) {
          const lookupCities = cityList.slice(0, 250);
          const fallbackPayloads = await Promise.all(
            lookupCities.map((city) =>
              fetchJson(`${PINCODE_API_BASE}/postal-search?q=${encodeURIComponent(city)}&country=IN`).catch(() => null)
            )
          );
          const fallbackRecords = mergeLocationRecords(
            ...fallbackPayloads.map((payload) => filterRecordsByState(extractLocationRecords(payload), state))
          );
          records = mergeLocationRecords(records, fallbackRecords);
          districtList = uniqueList([
            ...districtList,
            ...fallbackRecords.map((record) => record.district),
          ]).sort((a, b) => a.localeCompare(b));
        }

        setDistricts(districtList);
        setCities(cityList);
        setStatePostalRecords(records);
      })
      .catch(() => {
        if (!alive) return;
        setDistricts([]);
        setCities([]);
        setStatePostalRecords([]);
      })
      .finally(() => {
        if (!alive) return;
        setDistrictLoading(false);
        setCityLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [draftLocation.state]);

  useEffect(() => {
    let alive = true;
    const state = cleanName(draftLocation.state);
    const district = cleanName(draftLocation.district);
    setDistrictCities([]);
    setPincodeOptions([]);
    if (!state || !district) return undefined;

    const localRecords = filterRecordsByDistrict(filterRecordsByState(statePostalRecords, state), district);
    const localCities = uniqueList(localRecords.map((record) => record.city)).sort((a, b) => a.localeCompare(b));
    const localPins = uniqueList(localRecords.map((record) => record.pincode).filter(Boolean))
      .map((pin) => pin.replace(/\D+/g, ""))
      .filter((pin) => /^\d{6}$/.test(pin))
      .sort((a, b) => a.localeCompare(b));

    if (localCities.length) setDistrictCities(localCities);
    if (localPins.length) setPincodeOptions(localPins);
    if (localCities.length && localPins.length) return undefined;

    setCityLoading(true);
    setPincodeLoading(true);
    fetchJson(`${PINCODE_API_BASE}/postal-search?q=${encodeURIComponent(district)}&country=IN`)
      .then((payload) => {
        if (!alive) return;
        const records = filterRecordsByDistrict(filterRecordsByState(extractLocationRecords(payload), state), district);
        const list = uniqueList(records.map((record) => record.city)).sort((a, b) => a.localeCompare(b));
        const pins = uniqueList(records.map((record) => record.pincode).filter(Boolean))
          .map((pin) => pin.replace(/\D+/g, ""))
          .filter((pin) => /^\d{6}$/.test(pin))
          .sort((a, b) => a.localeCompare(b));

        setDistrictCities(list);
        setPincodeOptions((prev) => uniqueList([...prev, ...pins]).sort((a, b) => a.localeCompare(b)));
        setStatePostalRecords((prev) => mergeLocationRecords(prev, records));
      })
      .catch(() => {
        if (alive) {
          setDistrictCities([]);
          setPincodeOptions([]);
        }
      })
      .finally(() => {
        if (alive) {
          setCityLoading(false);
          setPincodeLoading(false);
        }
      });

    return () => {
      alive = false;
    };
  }, [draftLocation.state, draftLocation.district]);

  useEffect(() => {
    let alive = true;
    const state = cleanName(draftLocation.state);
    const district = cleanName(draftLocation.district);
    const city = cleanName(draftLocation.city);
    if (!state || !city) return undefined;

    const localRecords = mergeLocationRecords(
      filterRecordsByState(statePostalRecords, state).filter((record) => {
        const cityMatches = record.city && token(record.city) === token(city);
        const districtMatches = !district || !record.district || token(record.district) === token(district);
        return cityMatches && districtMatches;
      })
    );

    const localPins = uniqueList(localRecords.map((record) => record.pincode).filter(Boolean))
      .map((pin) => pin.replace(/\D+/g, ""))
      .filter((pin) => /^\d{6}$/.test(pin))
      .sort((a, b) => a.localeCompare(b));

    if (localPins.length) {
      setPincodeOptions(localPins);
      if (localPins.length === 1 && !cleanName(draftLocation.pincode)) {
        setDraftLocation((prev) => ({ ...prev, pincode: localPins[0] }));
      }
      return undefined;
    }

    setPincodeLoading(true);
    fetchJson(`${PINCODE_API_BASE}/postal-search?q=${encodeURIComponent(city)}&country=IN`)
      .then((payload) => {
        if (!alive) return;
        const records = filterRecordsByState(extractLocationRecords(payload), state).filter((record) => {
          const cityMatches = !record.city || token(record.city) === token(city);
          const districtMatches = !district || !record.district || token(record.district) === token(district);
          return cityMatches && districtMatches;
        });

        const pins = uniqueList(records.map((record) => record.pincode).filter(Boolean))
          .map((pin) => pin.replace(/\D+/g, ""))
          .filter((pin) => /^\d{6}$/.test(pin))
          .sort((a, b) => a.localeCompare(b));

        setPincodeOptions(pins);
        if (pins.length === 1 && !cleanName(draftLocation.pincode)) {
          setDraftLocation((prev) => ({ ...prev, pincode: pins[0] }));
        }
        if (records.length) setStatePostalRecords((prev) => mergeLocationRecords(prev, records));
      })
      .catch(() => {
        if (alive) setPincodeOptions([]);
      })
      .finally(() => {
        if (alive) setPincodeLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [draftLocation.state, draftLocation.district, draftLocation.city]);

  const availableCities = useMemo(() => {
    if (!draftLocation.state || !draftLocation.district) return [];
    return districtCities.length ? districtCities : cities;
  }, [draftLocation.state, draftLocation.district, districtCities, cities]);

  function updateGroceryEnabled(next) {
    setGroceryEnabled(next);
    if (!next && !foodEnabled) setFoodEnabled(true);
    setMessage("");
    setError("");
  }

  function updateFoodEnabled(next) {
    setFoodEnabled(next);
    if (!next && !groceryEnabled) setGroceryEnabled(true);
    setMessage("");
    setError("");
  }

  function updateFoodFeeRule(key, value) {
    setFoodDeliveryFeeRule((prev) => ({ ...prev, [key]: value }));
    setMessage("");
    setError("");
  }

  function updateFoodDriverEarningRule(key, value) {
    setFoodDriverEarningRule((prev) => ({ ...prev, [key]: value }));
    setMessage("");
    setError("");
  }

  function updateFoodRainChargeRule(key, value) {
    setFoodRainChargeRule((prev) => ({ ...prev, [key]: value }));
    setMessage("");
    setError("");
  }

  function updateFoodRainRestaurantOverride(index, key, value) {
    setFoodRainChargeRule((prev) => ({
      ...prev,
      restaurantOverrides: (prev.restaurantOverrides || []).map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        if (key === "restaurantId") {
          const selected = restaurants.find((restaurant) => String(restaurant.id) === String(value));
          return {
            ...item,
            restaurantId: value,
            restaurantName: selected?.name || item.restaurantName || "",
          };
        }
        if (key === "enabled") return { ...item, enabled: Boolean(value) };
        if (key === "currency") return { ...item, currency: String(value || "INR").toUpperCase() };
        return { ...item, [key]: value };
      }),
    }));
    setMessage("");
    setError("");
  }

  function addFoodRainRestaurantOverride() {
    setFoodRainChargeRule((prev) => ({
      ...prev,
      restaurantOverrides: addEmptyRestaurantRainOverride(prev.restaurantOverrides),
    }));
    setMessage("");
    setError("");
  }

  function removeFoodRainRestaurantOverride(index) {
    setFoodRainChargeRule((prev) => ({
      ...prev,
      restaurantOverrides: (prev.restaurantOverrides || []).filter((_, itemIndex) => itemIndex !== index),
    }));
    setMessage("");
    setError("");
  }

  function updateDraftLocation(key, value) {
    setDraftLocation((prev) => {
      if (key === "state") return { ...prev, state: value, district: "", city: "", pincode: "" };
      if (key === "district") return { ...prev, district: value, city: "", pincode: "" };
      if (key === "city") return { ...prev, city: value, pincode: "" };
      if (key === "pincode") return { ...prev, pincode: value.replace(/\D+/g, "").slice(0, 6) };
      return { ...prev, [key]: value };
    });
    setMessage("");
    setError("");
  }

  async function findByPincode() {
    const pincode = cleanName(draftLocation.pincode).replace(/\D+/g, "");
    if (!/^\d{6}$/.test(pincode)) {
      setError("Enter a valid 6 digit pincode first.");
      return;
    }

    setLocationLoading(true);
    setError("");
    setMessage("");
    try {
      const payload = await fetchJson(`${PINCODE_API_BASE}/postal/${pincode}?country=IN`);
      const record = findPostalRecord(payload);
      const location = getPostalLocation(record || {});
      if (!location.state && !location.city && !location.district) {
        throw new Error("Location not found for this pincode.");
      }
      setDraftLocation((prev) => ({
        ...prev,
        state: location.state || prev.state,
        district: location.district || prev.district,
        city: location.city || prev.city,
        pincode,
      }));
      if (location.district) setDistricts((prev) => uniqueList([...prev, location.district]).sort((a, b) => a.localeCompare(b)));
      if (location.city) setDistrictCities((prev) => uniqueList([...prev, location.city]).sort((a, b) => a.localeCompare(b)));
      if (pincode) setPincodeOptions((prev) => uniqueList([pincode, ...prev]).sort((a, b) => a.localeCompare(b)));
      if (location.state || location.district || location.city || location.pincode) {
        setStatePostalRecords((prev) => mergeLocationRecords(prev, [location]));
      }
    } catch (e) {
      setError(e?.message || "Unable to find location for this pincode.");
    } finally {
      setLocationLoading(false);
    }
  }

  function addLocationRule() {
    const next = {
      state: cleanName(draftLocation.state),
      district: cleanName(draftLocation.district),
      city: cleanName(draftLocation.city),
      pincode: cleanName(draftLocation.pincode).replace(/\D+/g, ""),
    };

    if (!next.state) {
      setError("Select a state before adding a Food visibility rule.");
      return;
    }
    if (next.pincode && !/^\d{6}$/.test(next.pincode)) {
      setError("Pincode must be 6 digits.");
      return;
    }

    const stateKey = token(next.state);
    const existing = foodLocationRules.find((rule) => token(rule.state) === stateKey);
    if (existing && !existing.districts.length && !existing.cities.length && !existing.pincodes.length) {
      setError(`${existing.state} is already allowed as a full state. Remove it first to add district/city/pincode rules.`);
      return;
    }

    setFoodLocationRules((prev) => {
      const rules = normalizeFinalLocationRules(prev);
      const index = rules.findIndex((rule) => token(rule.state) === stateKey);
      const rule = index >= 0 ? { ...rules[index] } : { state: next.state, districts: [], cities: [], pincodes: [] };

      if (!next.district && !next.city && !next.pincode) {
        rule.districts = [];
        rule.cities = [];
        rule.pincodes = [];
      } else {
        if (next.district) rule.districts = uniqueList([...rule.districts, next.district]);
        if (next.city) rule.cities = uniqueList([...rule.cities, next.city]);
        if (next.pincode) rule.pincodes = uniqueList([...rule.pincodes, next.pincode]);
      }

      if (index >= 0) rules[index] = rule;
      else rules.push(rule);
      return normalizeFinalLocationRules(rules);
    });

    setDraftLocation({ state: "", district: "", city: "", pincode: "" });
    setError("");
    setMessage("");
  }

  function removeLocationRule(state) {
    setFoodLocationRules((prev) => prev.filter((rule) => token(rule.state) !== token(state)));
    setMessage("");
    setError("");
  }

  function removeLocationValue(state, key, value) {
    setFoodLocationRules((prev) => normalizeFinalLocationRules(prev.map((rule) => {
      if (token(rule.state) !== token(state)) return rule;
      return {
        ...rule,
        [key]: rule[key].filter((item) => token(item) !== token(value)),
      };
    })).filter((rule) => rule.state));
    setMessage("");
    setError("");
  }

  async function save() {
    const finalGroceryEnabled = groceryEnabled || !foodEnabled;
    const finalFoodEnabled = foodEnabled || !finalGroceryEnabled;
    const defaultStore = finalGroceryEnabled ? "grocery" : "food";
    const finalDeliveryRadiusKm = Math.min(100, Math.max(0.1, Number(deliveryRadiusKm || 5)));
    const finalMinimumOrderPrice = Math.max(0, Number(minimumOrderPrice || 0));
    const finalSmallCartCharge = {
      enabled: Boolean(smallCartChargeEnabled),
      amount: Math.max(0, Number(smallCartChargeAmount || 0)),
    };
    const finalPlatformFee = Math.max(0, Number(platformFee || 0));
    const finalPackagingCharge = Math.max(0, Number(packagingCharge || 0));
    const finalPackagingChargeApplyAbove = Math.max(0, Number(packagingChargeApplyAbove || 0));
    const finalFoodDeliveryFeeRule = normalizeDeliveryFeeRule(foodDeliveryFeeRule);
    const finalFoodDriverEarningRule = normalizeDriverEarningRule(foodDriverEarningRule);
    const finalFoodRainChargeRule = normalizeRainChargeRule(foodRainChargeRule);
    const finalFoodLocationRules = normalizeFinalLocationRules(foodLocationRules);
    const finalFoodEnabledStates = uniqueList(finalFoodLocationRules.map((rule) => rule.state));
    const finalLocationAccess = {
      locations: finalFoodLocationRules,
      rules: finalFoodLocationRules,
      allowedLocations: finalFoodLocationRules,
      allowed_locations: finalFoodLocationRules,
      enabledStates: finalFoodEnabledStates,
      enabled_states: finalFoodEnabledStates,
      restricted: finalFoodLocationRules.length > 0,
    };

    setSaving(true);
    setError("");
    setMessage("");

    try {
      await restaurantAdminService.saveBusinessMode({
        groceryEnabled: finalGroceryEnabled,
        grocery_enabled: finalGroceryEnabled,
        foodEnabled: finalFoodEnabled,
        food_enabled: finalFoodEnabled,
        foodEnabledStates: finalFoodEnabledStates,
        food_enabled_states: finalFoodEnabledStates,
        foodAllowedLocations: finalFoodLocationRules,
        food_allowed_locations: finalFoodLocationRules,
        foodLocationAccess: finalLocationAccess,
        food_location_access: finalLocationAccess,
        foodStateAccess: finalLocationAccess,
        food_state_access: finalLocationAccess,
        defaultStore,
        default_store: defaultStore,
        deliveryRadiusKm: finalDeliveryRadiusKm,
        delivery_radius_km: finalDeliveryRadiusKm,
        minimumOrderPrice: finalMinimumOrderPrice,
        minimum_order_price: finalMinimumOrderPrice,
        smallCartCharge: finalSmallCartCharge,
        platformFee: finalPlatformFee,
        platform_fee: finalPlatformFee,
        foodPlatformFee: finalPlatformFee,
        food_platform_fee: finalPlatformFee,
        packagingCharge: finalPackagingCharge,
        packaging_charge: finalPackagingCharge,
        foodPackagingCharge: finalPackagingCharge,
        food_packaging_charge: finalPackagingCharge,
        packagingChargeApplyAbove: finalPackagingChargeApplyAbove,
        packaging_charge_apply_above: finalPackagingChargeApplyAbove,
        packagingChargeMinOrderValue: finalPackagingChargeApplyAbove,
        packaging_charge_min_order_value: finalPackagingChargeApplyAbove,
        foodDeliveryFeeRule: finalFoodDeliveryFeeRule,
        food_delivery_fee_rule: finalFoodDeliveryFeeRule,
        foodDeliveryFee: finalFoodDeliveryFeeRule,
        food_delivery_fee: finalFoodDeliveryFeeRule,
        foodDriverEarningRule: finalFoodDriverEarningRule,
        food_driver_earning_rule: finalFoodDriverEarningRule,
        foodDriverEarning: finalFoodDriverEarningRule,
        food_driver_earning: finalFoodDriverEarningRule,
        foodRainCharge: finalFoodRainChargeRule,
        food_rain_charge: finalFoodRainChargeRule,
        deliveryFeeRules: {
          food: finalFoodDeliveryFeeRule,
        },
        delivery_fee_rules: {
          food: finalFoodDeliveryFeeRule,
        },
        driverEarningRules: {
          food: finalFoodDriverEarningRule,
        },
        driver_earning_rules: {
          food: finalFoodDriverEarningRule,
        },
        businessMode: {
          groceryEnabled: finalGroceryEnabled,
          grocery_enabled: finalGroceryEnabled,
          foodEnabled: finalFoodEnabled,
          food_enabled: finalFoodEnabled,
          foodEnabledStates: finalFoodEnabledStates,
          food_enabled_states: finalFoodEnabledStates,
          foodAllowedLocations: finalFoodLocationRules,
          food_allowed_locations: finalFoodLocationRules,
          foodLocationAccess: finalLocationAccess,
          food_location_access: finalLocationAccess,
          foodStateAccess: finalLocationAccess,
          food_state_access: finalLocationAccess,
          foodStateRestricted: finalFoodLocationRules.length > 0,
          food_state_restricted: finalFoodLocationRules.length > 0,
          foodLocationRestricted: finalFoodLocationRules.length > 0,
          food_location_restricted: finalFoodLocationRules.length > 0,
          defaultStore,
          default_store: defaultStore,
          platformFee: finalPlatformFee,
          platform_fee: finalPlatformFee,
          foodPlatformFee: finalPlatformFee,
          food_platform_fee: finalPlatformFee,
          packagingCharge: finalPackagingCharge,
          packaging_charge: finalPackagingCharge,
          foodPackagingCharge: finalPackagingCharge,
          food_packaging_charge: finalPackagingCharge,
          packagingChargeApplyAbove: finalPackagingChargeApplyAbove,
          packaging_charge_apply_above: finalPackagingChargeApplyAbove,
          packagingChargeMinOrderValue: finalPackagingChargeApplyAbove,
          packaging_charge_min_order_value: finalPackagingChargeApplyAbove,
          foodDeliveryFeeRule: finalFoodDeliveryFeeRule,
          foodDeliveryFee: finalFoodDeliveryFeeRule,
          foodDriverEarningRule: finalFoodDriverEarningRule,
          food_driver_earning_rule: finalFoodDriverEarningRule,
          foodDriverEarning: finalFoodDriverEarningRule,
          food_driver_earning: finalFoodDriverEarningRule,
          driverEarningRules: {
            food: finalFoodDriverEarningRule,
          },
          driver_earning_rules: {
            food: finalFoodDriverEarningRule,
          },
          foodRainCharge: finalFoodRainChargeRule,
          food_rain_charge: finalFoodRainChargeRule,
          foodDeliverySettings: {
            deliveryRadiusKm: finalDeliveryRadiusKm,
            delivery_radius_km: finalDeliveryRadiusKm,
            minimumOrderPrice: finalMinimumOrderPrice,
            minimum_order_price: finalMinimumOrderPrice,
            smallCartCharge: finalSmallCartCharge,
            platformFee: finalPlatformFee,
            platform_fee: finalPlatformFee,
            foodPlatformFee: finalPlatformFee,
            food_platform_fee: finalPlatformFee,
            packagingCharge: finalPackagingCharge,
            packaging_charge: finalPackagingCharge,
            foodPackagingCharge: finalPackagingCharge,
            food_packaging_charge: finalPackagingCharge,
            packagingChargeApplyAbove: finalPackagingChargeApplyAbove,
            packaging_charge_apply_above: finalPackagingChargeApplyAbove,
            packagingChargeMinOrderValue: finalPackagingChargeApplyAbove,
            packaging_charge_min_order_value: finalPackagingChargeApplyAbove,
            deliveryFeeRule: finalFoodDeliveryFeeRule,
            delivery_fee_rule: finalFoodDeliveryFeeRule,
            deliveryFee: finalFoodDeliveryFeeRule,
            delivery_fee: finalFoodDeliveryFeeRule,
            driverEarningRule: finalFoodDriverEarningRule,
            driver_earning_rule: finalFoodDriverEarningRule,
            driverEarning: finalFoodDriverEarningRule,
            driver_earning: finalFoodDriverEarningRule,
            rainCharge: finalFoodRainChargeRule,
            rain_charge: finalFoodRainChargeRule,
          },
        },
        foodDeliverySettings: {
          deliveryRadiusKm: finalDeliveryRadiusKm,
          delivery_radius_km: finalDeliveryRadiusKm,
          minimumOrderPrice: finalMinimumOrderPrice,
          minimum_order_price: finalMinimumOrderPrice,
          smallCartCharge: finalSmallCartCharge,
          platformFee: finalPlatformFee,
          platform_fee: finalPlatformFee,
          foodPlatformFee: finalPlatformFee,
          food_platform_fee: finalPlatformFee,
          packagingCharge: finalPackagingCharge,
          packaging_charge: finalPackagingCharge,
          foodPackagingCharge: finalPackagingCharge,
          food_packaging_charge: finalPackagingCharge,
          packagingChargeApplyAbove: finalPackagingChargeApplyAbove,
          packaging_charge_apply_above: finalPackagingChargeApplyAbove,
          packagingChargeMinOrderValue: finalPackagingChargeApplyAbove,
          packaging_charge_min_order_value: finalPackagingChargeApplyAbove,
          deliveryFeeRule: finalFoodDeliveryFeeRule,
          delivery_fee_rule: finalFoodDeliveryFeeRule,
          deliveryFee: finalFoodDeliveryFeeRule,
          delivery_fee: finalFoodDeliveryFeeRule,
          driverEarningRule: finalFoodDriverEarningRule,
          driver_earning_rule: finalFoodDriverEarningRule,
          driverEarning: finalFoodDriverEarningRule,
          driver_earning: finalFoodDriverEarningRule,
          rainCharge: finalFoodRainChargeRule,
          rain_charge: finalFoodRainChargeRule,
        },
        food_delivery_settings: {
          deliveryRadiusKm: finalDeliveryRadiusKm,
          delivery_radius_km: finalDeliveryRadiusKm,
          minimumOrderPrice: finalMinimumOrderPrice,
          minimum_order_price: finalMinimumOrderPrice,
          platformFee: finalPlatformFee,
          platform_fee: finalPlatformFee,
          foodPlatformFee: finalPlatformFee,
          food_platform_fee: finalPlatformFee,
          packagingCharge: finalPackagingCharge,
          packaging_charge: finalPackagingCharge,
          foodPackagingCharge: finalPackagingCharge,
          food_packaging_charge: finalPackagingCharge,
          packagingChargeApplyAbove: finalPackagingChargeApplyAbove,
          packaging_charge_apply_above: finalPackagingChargeApplyAbove,
          packagingChargeMinOrderValue: finalPackagingChargeApplyAbove,
          packaging_charge_min_order_value: finalPackagingChargeApplyAbove,
          deliveryFeeRule: finalFoodDeliveryFeeRule,
          delivery_fee_rule: finalFoodDeliveryFeeRule,
          deliveryFee: finalFoodDeliveryFeeRule,
          delivery_fee: finalFoodDeliveryFeeRule,
          driverEarningRule: finalFoodDriverEarningRule,
          driver_earning_rule: finalFoodDriverEarningRule,
          driverEarning: finalFoodDriverEarningRule,
          driver_earning: finalFoodDriverEarningRule,
          rainCharge: finalFoodRainChargeRule,
          rain_charge: finalFoodRainChargeRule,
        },
      });
      await saveSavedFoodDeliveryFeeRule(finalFoodDeliveryFeeRule);
      setGroceryEnabled(finalGroceryEnabled);
      setFoodEnabled(finalFoodEnabled);
      setFoodLocationRules(finalFoodLocationRules);
      setDeliveryRadiusKm(finalDeliveryRadiusKm);
      setMinimumOrderPrice(finalMinimumOrderPrice);
      setSmallCartChargeEnabled(finalSmallCartCharge.enabled);
      setSmallCartChargeAmount(finalSmallCartCharge.amount);
      setPlatformFee(finalPlatformFee);
      setPackagingCharge(finalPackagingCharge);
      setPackagingChargeApplyAbove(finalPackagingChargeApplyAbove);
      setFoodDeliveryFeeRule(finalFoodDeliveryFeeRule);
      setFoodDriverEarningRule(finalFoodDriverEarningRule);
      setFoodRainChargeRule(finalFoodRainChargeRule);
      setMessage("Settings saved successfully");
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  }

  const sortedRules = useMemo(() => normalizeFinalLocationRules(foodLocationRules), [foodLocationRules]);

  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle="Control module visibility, delivery radius, minimum order value, platform fee, packaging fee, delivery fee rules, and driver earning rules."
        actions={
          <Button onClick={save} disabled={saving || loading}>
            <Save size={16} /> {saving ? "Saving..." : "Save Settings"}
          </Button>
        }
      />

      <ErrorBox error={error} />

      {message ? (
        <div className="mb-4 rounded-[16px] border border-emerald-200 bg-emerald-50 p-4 text-[13px] font-bold text-emerald-700">
          {message}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <Card>
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-[20px] font-bold text-[#d86a78]">Grocery Module</h2>
              <p className="mt-1 text-[13px] text-slate-500">
                Show existing grocery home and products. Default module when enabled.
              </p>
            </div>
            <Switch checked={groceryEnabled} onChange={updateGroceryEnabled} />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-[20px] font-bold text-[#d86a78]">Module Visibility</h2>
              <p className="mt-1 text-[13px] text-slate-500">
                Show restaurant/food home, banners, deals and restaurants.
              </p>
            </div>
            <Switch checked={foodEnabled} onChange={updateFoodEnabled} />
          </div>
        </Card>
      </div>

      <div className="mt-5">
        <Card>
          <div className="flex flex-col gap-5">
            <div className="flex items-start gap-3">
              <div className="rounded-[16px] bg-rose-50 p-3 text-rose-600">
                <MapPin size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-[20px] font-bold text-[#d86a78]">Food Location Visibility</h2>
                <p className="mt-1 text-[13px] text-slate-500">
                  Control where Food is visible. Empty rules show Food everywhere. Add only a state to allow the full state, or narrow it by district, city, or pincode.
                </p>
              </div>
            </div>

            <div className="rounded-[22px] border border-rose-100 bg-rose-50/30 p-4">
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
                <SearchableDropdown
                  label="State"
                  value={draftLocation.state}
                  onChange={(value) => updateDraftLocation("state", value)}
                  options={states}
                  placeholder="Select state"
                  loading={!states.length}
                />
                <SearchableDropdown
                  label="District"
                  value={draftLocation.district}
                  onChange={(value) => updateDraftLocation("district", value)}
                  options={districts}
                  placeholder={draftLocation.state ? "Select district" : "Select state first"}
                  disabled={!draftLocation.state}
                  loading={districtLoading}
                  allowCustom
                  emptyText="No districts found. Search pincode or type district name."
                />
                <SearchableDropdown
                  label="City"
                  value={draftLocation.city}
                  onChange={(value) => updateDraftLocation("city", value)}
                  options={availableCities}
                  placeholder={!draftLocation.state ? "Select state first" : !draftLocation.district ? "Select district first" : "Select city"}
                  disabled={!draftLocation.state || !draftLocation.district}
                  loading={cityLoading && Boolean(draftLocation.district)}
                />
                <SearchableDropdown
                  label="Pincode"
                  value={draftLocation.pincode}
                  onChange={(value) => updateDraftLocation("pincode", value)}
                  options={pincodeOptions}
                  placeholder={draftLocation.city || draftLocation.district ? "Select or enter pincode" : "Enter pincode"}
                  loading={pincodeLoading}
                  allowCustom
                  emptyText="No pincode found. Type 6 digit pincode and use Find by Pincode."
                />
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={addLocationRule}
                  className="inline-flex items-center gap-2 rounded-[16px] bg-[#d86a78] px-4 py-3 text-[13px] font-black text-white shadow-lg shadow-rose-100 transition hover:brightness-95"
                >
                  <Plus size={15} /> Add Location Rule
                </button>
                <button
                  type="button"
                  onClick={findByPincode}
                  disabled={locationLoading}
                  className="inline-flex items-center gap-2 rounded-[16px] border border-rose-100 bg-white px-4 py-3 text-[13px] font-black text-[#d86a78] transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {locationLoading ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
                  Find by Pincode
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDraftLocation({ state: "", district: "", city: "", pincode: "" });
                    setFoodLocationRules([]);
                    setMessage("");
                    setError("");
                  }}
                  className="inline-flex items-center gap-2 rounded-[16px] border border-slate-200 bg-white px-4 py-3 text-[13px] font-black text-slate-500 transition hover:bg-slate-50"
                >
                  Clear Rules
                </button>
              </div>

              {/* <p className="mt-3 text-[12px] font-semibold text-slate-400">
                Data helper uses pincode.indraq.info for India states, cities and postal lookup. Select state first, then district, then city. Pincode suggestions auto-load after city/district when the API returns postal records. Matching priority: pincode, city, district, then state.
              </p> */}
            </div>

            <div className="rounded-[22px] border border-rose-100 bg-white">
              <div className="flex items-center justify-between gap-3 border-b border-rose-50 px-4 py-3">
                <div>
                  <h3 className="text-[14px] font-black text-slate-700">Added Location Rules</h3>
                  <p className="text-[12px] font-semibold text-slate-400">This area scrolls when many states or pincodes are added.</p>
                </div>
                <span className="rounded-full bg-rose-50 px-3 py-1 text-[12px] font-black text-[#d86a78]">{sortedRules.length} rules</span>
              </div>

              <div className="max-h-[360px] overflow-y-auto overscroll-contain p-4">
                {sortedRules.length ? (
                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    {sortedRules.map((rule) => {
                      const fullState = !rule.districts.length && !rule.cities.length && !rule.pincodes.length;
                      return (
                        <div key={rule.state} className="rounded-[20px] border border-rose-100 bg-white p-4 shadow-sm shadow-rose-50">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <h4 className="truncate text-[15px] font-black text-slate-800">{rule.state}</h4>
                              <p className="mt-1 text-[12px] font-bold text-slate-400">
                                {fullState ? "Full state is allowed." : "Limited inside this state by selected districts/cities/pincodes."}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeLocationRule(rule.state)}
                              className="shrink-0 rounded-full bg-rose-50 px-3 py-2 text-[12px] font-black text-[#d86a78] hover:bg-rose-100"
                            >
                              Remove State
                            </button>
                          </div>

                          <div className="mt-4 space-y-3">
                            {fullState ? (
                              <Chip>Full state</Chip>
                            ) : null}
                            {rule.districts.length ? (
                              <div>
                                <div className="mb-2 text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Districts</div>
                                <div className="flex max-h-[96px] flex-wrap gap-2 overflow-y-auto pr-1">
                                  {rule.districts.map((district) => (
                                    <Chip key={district} onRemove={() => removeLocationValue(rule.state, "districts", district)}>{district}</Chip>
                                  ))}
                                </div>
                              </div>
                            ) : null}
                            {rule.cities.length ? (
                              <div>
                                <div className="mb-2 text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Cities</div>
                                <div className="flex max-h-[96px] flex-wrap gap-2 overflow-y-auto pr-1">
                                  {rule.cities.map((city) => (
                                    <Chip key={city} onRemove={() => removeLocationValue(rule.state, "cities", city)}>{city}</Chip>
                                  ))}
                                </div>
                              </div>
                            ) : null}
                            {rule.pincodes.length ? (
                              <div>
                                <div className="mb-2 text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Pincodes</div>
                                <div className="flex max-h-[96px] flex-wrap gap-2 overflow-y-auto pr-1">
                                  {rule.pincodes.map((pincode) => (
                                    <Chip key={pincode} onRemove={() => removeLocationValue(rule.state, "pincodes", pincode)}>{pincode}</Chip>
                                  ))}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex min-h-[120px] items-center justify-center rounded-[18px] bg-slate-50 text-center text-[13px] font-bold text-slate-400">
                    No location rule added. Food will be visible everywhere when Food module is ON.
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
        <Card>
          <div className="flex items-start gap-3">
            <div className="rounded-[16px] bg-rose-50 p-3 text-rose-600">
              <MapPin size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-[20px] font-bold text-[#d86a78]">Delivery Radius</h2>
              <p className="mt-1 text-[13px] text-slate-500">
                User will see only restaurants inside this radius from their selected delivery location.
              </p>
              <div className="mt-4 flex items-center gap-3 rounded-[18px] border border-rose-100 bg-white px-4 py-3">
                <input
                  type="number"
                  min="0.1"
                  max="100"
                  step="0.1"
                  value={deliveryRadiusKm}
                  onChange={(e) => setDeliveryRadiusKm(e.target.value)}
                  className="w-full bg-transparent text-[15px] font-semibold text-[#d86a78] outline-none"
                  placeholder="5"
                />
                <span className="shrink-0 text-sm font-semibold uppercase tracking-[0.18em] text-slate-400">KM</span>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-start gap-3">
            <div className="rounded-[16px] bg-amber-50 p-3 text-amber-600">
              <IndianRupee size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-[20px] font-bold text-[#d86a78]">Small Cart Charge</h2>
                  <p className="mt-1 text-[13px] text-slate-500">
                    Reuses the existing minimum order as the threshold. Toggle off to keep the legacy hard minimum block.
                  </p>
                </div>
                <Switch checked={smallCartChargeEnabled} onChange={setSmallCartChargeEnabled} />
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <label className="rounded-[18px] border border-rose-100 bg-white px-4 py-3">
                  <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-400">Threshold</span>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-[18px] font-semibold text-slate-400">₹</span>
                    <input
                      type="number"
                      min="0"
                      max="100000"
                      step="1"
                      value={minimumOrderPrice}
                      onChange={(e) => setMinimumOrderPrice(e.target.value)}
                      className="w-full bg-transparent text-[15px] font-semibold text-[#d86a78] outline-none"
                      placeholder="199"
                    />
                  </div>
                </label>
                <label className="rounded-[18px] border border-rose-100 bg-white px-4 py-3">
                  <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-400">Fixed charge</span>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-[18px] font-semibold text-slate-400">₹</span>
                    <input
                      type="number"
                      min="0"
                      max="100000"
                      step="1"
                      value={smallCartChargeAmount}
                      onChange={(e) => setSmallCartChargeAmount(e.target.value)}
                      className="w-full bg-transparent text-[15px] font-semibold text-[#d86a78] outline-none"
                      placeholder="20"
                    />
                  </div>
                </label>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-3">
        <Card>
          <div className="flex items-start gap-3">
            <div className="rounded-[16px] bg-sky-50 p-3 text-sky-600">
              <IndianRupee size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-[20px] font-bold text-[#d86a78]">Platform Fee</h2>
              <p className="mt-1 text-[13px] text-slate-500">
                Fixed platform fee charged to the customer for food orders from this dashboard setting.
              </p>
              <div className="mt-4 flex items-center gap-3 rounded-[18px] border border-sky-100 bg-white px-4 py-3">
                <span className="shrink-0 text-[18px] font-semibold text-slate-400">₹</span>
                <input
                  type="number"
                  min="0"
                  max="100000"
                  step="1"
                  value={platformFee}
                  onChange={(e) => setPlatformFee(e.target.value)}
                  className="w-full text-[18px] font-semibold text-[#d86a78] outline-none"
                  placeholder="0"
                />
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-start gap-3">
            <div className="rounded-[16px] bg-emerald-50 p-3 text-emerald-600">
              <PackageCheck size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-[20px] font-bold text-[#d86a78]">Packaging Fee</h2>
              <p className="mt-1 text-[13px] text-slate-500">
                Fixed packaging fee charged from this dashboard setting. Restaurant-level packaging fee is ignored in user billing.
              </p>
              <div className="mt-4 flex items-center gap-3 rounded-[18px] border border-emerald-100 bg-white px-4 py-3">
                <span className="shrink-0 text-[18px] font-semibold text-slate-400">₹</span>
                <input
                  type="number"
                  min="0"
                  max="100000"
                  step="1"
                  value={packagingCharge}
                  onChange={(e) => setPackagingCharge(e.target.value)}
                  className="w-full text-[18px] font-semibold text-[#d86a78] outline-none"
                  placeholder="0"
                />
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-start gap-3">
            <div className="rounded-[16px] bg-amber-50 p-3 text-amber-600">
              <PackageCheck size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-[20px] font-bold text-[#d86a78]">Packaging Applies From</h2>
              <p className="mt-1 text-[13px] text-slate-500">
                Packaging fee will apply only when food item subtotal reaches this amount. Set 0 to apply on every food order.
              </p>
              <div className="mt-4 flex items-center gap-3 rounded-[18px] border border-amber-100 bg-white px-4 py-3">
                <span className="shrink-0 text-[18px] font-semibold text-slate-400">₹</span>
                <input
                  type="number"
                  min="0"
                  max="100000"
                  step="1"
                  value={packagingChargeApplyAbove}
                  onChange={(e) => setPackagingChargeApplyAbove(e.target.value)}
                  className="w-full text-[18px] font-semibold text-[#d86a78] outline-none"
                  placeholder="0"
                />
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="mt-5">
        <Card>
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="rounded-[16px] bg-sky-50 p-3 text-sky-600">
                  <Truck size={20} />
                </div>
                <div>
                  <h2 className="text-[20px] font-bold text-[#d86a78]">Delivery Fee Rules</h2>
                  <p className="mt-1 text-[13px] text-slate-500">
                    Separate customer delivery fee for food orders.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-[18px] border border-rose-100 bg-white px-4 py-3">
                <span className="text-sm font-black text-slate-700">Enable Delivery Fee</span>
                <Switch checked={Boolean(foodDeliveryFeeRule.enabled)} onChange={(next) => updateFoodFeeRule("enabled", next)} />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <label className="block">
                <span className="text-[13px] font-bold text-slate-700">Currency</span>
                <div className="mt-2 rounded-[18px] border border-rose-100 bg-white px-4 py-3">
                  <input
                    value={foodDeliveryFeeRule.currency}
                    onChange={(e) => updateFoodFeeRule("currency", e.target.value)}
                    className="w-full bg-transparent text-[15px] font-semibold text-[#d86a78] outline-none"
                    placeholder="INR"
                  />
                </div>
              </label>
              <FeeInput label="Base Km" value={foodDeliveryFeeRule.baseKm} onChange={(v) => updateFoodFeeRule("baseKm", v)} step="0.1" suffix="KM" placeholder="3" />
              <FeeInput label="Base Fee" value={foodDeliveryFeeRule.baseFee} onChange={(v) => updateFoodFeeRule("baseFee", v)} prefix="₹" placeholder="40" />
              <FeeInput label="Per Km Charge" value={foodDeliveryFeeRule.perKmCharge} onChange={(v) => updateFoodFeeRule("perKmCharge", v)} prefix="₹" placeholder="8" />
              <FeeInput label="Minimum Fee" value={foodDeliveryFeeRule.minimumFee} onChange={(v) => updateFoodFeeRule("minimumFee", v)} prefix="₹" placeholder="10" />
              <FeeInput label="Max Fee" value={foodDeliveryFeeRule.maxFee} onChange={(v) => updateFoodFeeRule("maxFee", v)} prefix="₹" placeholder="80" />
              <FeeInput label="Free Delivery Above" value={foodDeliveryFeeRule.freeDeliveryAbove} onChange={(v) => updateFoodFeeRule("freeDeliveryAbove", v)} prefix="₹" placeholder="500" />
              <FeeInput label="Round To" value={foodDeliveryFeeRule.roundTo} onChange={(v) => updateFoodFeeRule("roundTo", v)} prefix="₹" step="0.01" placeholder="1" />
            </div>
          </div>
        </Card>
      </div>


      <div className="mt-5">
        <Card>
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="rounded-[16px] bg-emerald-50 p-3 text-emerald-600">
                  <IndianRupee size={20} />
                </div>
                <div>
                  <h2 className="text-[20px] font-bold text-[#d86a78]">Driver Earning Rules</h2>
                  <p className="mt-1 text-[13px] text-slate-500">
                    Food driver payout rule. This is independent from the customer delivery fee.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-[18px] border border-rose-100 bg-white px-4 py-3">
                <span className="text-sm font-black text-slate-700">Enable Driver Earning</span>
                <Switch checked={Boolean(foodDriverEarningRule.enabled)} onChange={(next) => updateFoodDriverEarningRule("enabled", next)} />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <label className="block">
                <span className="text-[13px] font-bold text-slate-700">Currency</span>
                <div className="mt-2 rounded-[18px] border border-rose-100 bg-white px-4 py-3">
                  <input
                    value={foodDriverEarningRule.currency}
                    onChange={(e) => updateFoodDriverEarningRule("currency", e.target.value)}
                    className="w-full bg-transparent text-[15px] font-semibold text-[#d86a78] outline-none"
                    placeholder="INR"
                  />
                </div>
              </label>
              <label className="block">
                <span className="text-[13px] font-bold text-slate-700">Mode</span>
                <div className="mt-2 rounded-[18px] border border-rose-100 bg-white px-4 py-3">
                  <select
                    value={foodDriverEarningRule.mode || "DISTANCE_BASED"}
                    onChange={(e) => updateFoodDriverEarningRule("mode", e.target.value)}
                    className="w-full bg-transparent text-[15px] font-semibold text-[#d86a78] outline-none"
                  >
                    <option value="DISTANCE_BASED">Distance Based</option>
                    <option value="FIXED">Fixed Amount</option>
                  </select>
                </div>
              </label>
              <FeeInput label="Fixed Amount" value={foodDriverEarningRule.fixedAmount} onChange={(v) => updateFoodDriverEarningRule("fixedAmount", v)} prefix="₹" placeholder="35" />
              {(foodDriverEarningRule.mode || "DISTANCE_BASED") !== "FIXED" ? (
                <>
                  <FeeInput label="Base Km" value={foodDriverEarningRule.baseKm} onChange={(v) => updateFoodDriverEarningRule("baseKm", v)} step="0.1" suffix="KM" placeholder="3" />
                  <FeeInput label="Base Earning" value={foodDriverEarningRule.baseFee} onChange={(v) => updateFoodDriverEarningRule("baseFee", v)} prefix="₹" placeholder="25" />
                  <FeeInput label="Per Km Earning" value={foodDriverEarningRule.perKmCharge} onChange={(v) => updateFoodDriverEarningRule("perKmCharge", v)} prefix="₹" placeholder="8" />
                  <FeeInput label="Minimum Earning" value={foodDriverEarningRule.minimumFee} onChange={(v) => updateFoodDriverEarningRule("minimumFee", v)} prefix="₹" placeholder="25" />
                  <FeeInput label="Max Earning" value={foodDriverEarningRule.maxFee} onChange={(v) => updateFoodDriverEarningRule("maxFee", v)} prefix="₹" placeholder="100" />
                  <FeeInput label="Round To" value={foodDriverEarningRule.roundTo} onChange={(v) => updateFoodDriverEarningRule("roundTo", v)} prefix="₹" step="0.01" placeholder="1" />
                </>
              ) : null}
            </div>
          </div>
        </Card>
      </div>


      <div className="mt-5">
        <Card>
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="rounded-[16px] bg-indigo-50 p-3 text-indigo-600">
                  <IndianRupee size={20} />
                </div>
                <div>
                  <h2 className="text-[20px] font-bold text-[#d86a78]">Food Rain Charge</h2>
                  <p className="mt-1 text-[13px] text-slate-500">
                    Weather-based extra charge for food orders. Global rule applies to all restaurants; restaurant overrides can turn it on/off per restaurant.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-[18px] border border-rose-100 bg-white px-4 py-3">
                <span className="text-sm font-black text-slate-700">Enable Global Rain Charge</span>
                <Switch checked={Boolean(foodRainChargeRule.enabled)} onChange={(next) => updateFoodRainChargeRule("enabled", next)} />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <label className="block">
                <span className="text-[13px] font-bold text-slate-700">Currency</span>
                <div className="mt-2 rounded-[18px] border border-rose-100 bg-white px-4 py-3">
                  <input
                    value={foodRainChargeRule.currency}
                    onChange={(e) => updateFoodRainChargeRule("currency", e.target.value.toUpperCase())}
                    className="w-full bg-transparent text-[15px] font-semibold text-[#d86a78] outline-none"
                    placeholder="INR"
                  />
                </div>
              </label>
              <FeeInput label="Rain Charge Amount" value={foodRainChargeRule.amount} onChange={(v) => updateFoodRainChargeRule("amount", v)} prefix="₹" placeholder="0" />
              <FeeInput label="Min Order Value" value={foodRainChargeRule.minOrderValue} onChange={(v) => updateFoodRainChargeRule("minOrderValue", v)} prefix="₹" placeholder="0" />
              <FeeInput label="Min Rain Probability" value={foodRainChargeRule.minProbabilityPercent} onChange={(v) => updateFoodRainChargeRule("minProbabilityPercent", v)} suffix="%" placeholder="50" />
              <FeeInput label="Weather Cache TTL" value={foodRainChargeRule.cacheTtlSeconds} onChange={(v) => updateFoodRainChargeRule("cacheTtlSeconds", v)} suffix="sec" placeholder="300" />
            </div>

            <div className="rounded-[20px] border border-rose-100 bg-rose-50/30 p-4">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-[16px] font-black text-slate-800">Restaurant-wise Rain Charge Overrides</h3>
                  <p className="mt-1 text-[12px] font-semibold text-slate-500">Use this when one restaurant needs rain charge ON/OFF or a different amount.</p>
                </div>
                <Button type="button" onClick={addFoodRainRestaurantOverride}>
                  <Plus size={16} /> Add Restaurant Override
                </Button>
              </div>

              {(foodRainChargeRule.restaurantOverrides || []).length ? (
                <div className="space-y-3">
                  {(foodRainChargeRule.restaurantOverrides || []).map((row, index) => (
                    <div key={`food-rain-restaurant-${index}`} className="rounded-[18px] border border-rose-100 bg-white p-4">
                      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-black text-slate-700">Enabled</span>
                          <Switch checked={Boolean(row.enabled)} onChange={(next) => updateFoodRainRestaurantOverride(index, "enabled", next)} />
                        </div>
                        <button type="button" onClick={() => removeFoodRainRestaurantOverride(index)} className="rounded-[14px] border border-rose-100 px-3 py-2 text-sm font-black text-rose-600 hover:bg-rose-50">
                          Remove
                        </button>
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <label className="block">
                          <span className="text-[13px] font-bold text-slate-700">Restaurant</span>
                          <div className="mt-2 rounded-[18px] border border-rose-100 bg-white px-4 py-3">
                            <select
                              value={row.restaurantId}
                              onChange={(e) => updateFoodRainRestaurantOverride(index, "restaurantId", e.target.value)}
                              className="w-full bg-transparent text-[15px] font-semibold text-[#d86a78] outline-none"
                            >
                              <option value="">Select restaurant</option>
                              {restaurants.map((restaurant) => (
                                <option key={restaurant.id} value={restaurant.id}>{restaurant.name}</option>
                              ))}
                            </select>
                          </div>
                        </label>
                        <FeeInput label="Amount" value={row.amount} onChange={(v) => updateFoodRainRestaurantOverride(index, "amount", v)} prefix="₹" placeholder="0" />
                        <FeeInput label="Min Order Value" value={row.minOrderValue} onChange={(v) => updateFoodRainRestaurantOverride(index, "minOrderValue", v)} prefix="₹" placeholder="0" />
                        <FeeInput label="Min Rain Probability" value={row.minProbabilityPercent} onChange={(v) => updateFoodRainRestaurantOverride(index, "minProbabilityPercent", v)} suffix="%" placeholder="50" />
                        <FeeInput label="Weather Cache TTL" value={row.cacheTtlSeconds} onChange={(v) => updateFoodRainRestaurantOverride(index, "cacheTtlSeconds", v)} suffix="sec" placeholder="300" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-[18px] border border-dashed border-rose-200 bg-white px-4 py-6 text-center text-sm font-semibold text-slate-500">
                  No restaurant override added. Global food rain charge rule will apply.
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
